import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { CourseContext } from "./CourseContext";
import { SliderBigText } from "./SliderBigText";
import { TrainingProgram } from "./TrainingProgram";
import s from '../CourseDetail.module.css';
import { useAppTranslation } from "./TranslationContext";
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

/* Reveal hook — добавляет класс .visible при попадании в viewport */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add(s.visible);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* Stagger reveal — каждый дочерний элемент появляется по очереди */
function useStaggerReveal(count, delayStep = 0.1) {
  const refs = useRef([]);
  useEffect(() => {
    refs.current.forEach((el, i) => {
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => el.classList.add(s.visible), i * delayStep * 1000);
            observer.disconnect();
          }
        },
        { threshold: 0.08 }
      );
      observer.observe(el);
    });
  }, [count]);
  return refs;
}


export const CourseDetail = () => {
  const { user, handleLang } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const courses = useContext(CourseContext);
  const course = courses.find(c => c.id === parseInt(id, 10));
  const { t } = useAppTranslation();

  const [dataAvz, setDataAvz] = useState([]);
  const [avzalliklar, setAvzalliklar] = useState([]);
  const [dataDesc, setDataDesc] = useState([]);
  const [description, setDescription] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDesc, setLoadingDesc] = useState(true);
  const [error, setError] = useState(null);
  const [errorDesc, setErrorDesc] = useState(null);
  const [author, setAuthor] = useState(null);
  const [loadingAuthor, setLoadingAuthor] = useState(false);
  const [errorAuthor, setErrorAuthor] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const descRef = useReveal();
  const authorRef = useReveal();
  const featuresHeaderRef = useReveal(0.1);
  const featureItemRefs = useStaggerReveal(avzalliklar.length, 0.1);

  /* ── Fetch avzalliklar + description ── */
  useEffect(() => {
    fetch("https://api.aiassessment.uz/api/kurs-avzallik/")
      .then(res => { if (!res.ok) throw new Error(res.status); return res.json(); })
      .then(json => { setDataAvz(json); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });

    fetch("https://api.aiassessment.uz/api/kurs-big-description/")
      .then(res => { if (!res.ok) throw new Error(res.status); return res.json(); })
      .then(json => { setDataDesc(json); setLoadingDesc(false); })
      .catch(err => { setErrorDesc(err.message); setLoadingDesc(false); });
  }, [t]);

  useEffect(() => {
    if (!loading && Array.isArray(dataAvz) && dataAvz.length > 0) {
      const filtered = dataAvz
        .filter(item => String(item?.kurs?.id) === String(id))
        .map(i => ({
          id: i.id,
          title_uz: i.nomi_uz, title_ru: i.nomi_ru, title_en: i.nomi_en,
          desc_uz: i.description_uz, desc_ru: i.description_ru, desc_en: i.description_en,
        }));
      setAvzalliklar(filtered);
    }
  }, [dataAvz, loading, id]);

  useEffect(() => {
    if (!loadingDesc && Array.isArray(dataDesc) && dataDesc.length > 0) {
      const desc = dataDesc.find(i => String(i?.kurs?.id) === String(id));
      setDescription(desc ? {
        title_uz: desc.nomi_uz, title_ru: desc.nomi_ru, title_en: desc.nomi_en,
        img_uz: desc.img_uz, img_ru: desc.img_ru, img_en: desc.img_en,
        desc_uz: desc.description_uz, desc_ru: desc.description_ru, desc_en: desc.description_en,
      } : {});
    }
  }, [dataDesc, loadingDesc, id]);

  /* ── Fetch author ── */
  useEffect(() => {
    if (!course?.author?.id) return;
    setLoadingAuthor(true);
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(`https://api.aiassessment.uz/api/custom-users/?user=${course.author.id}`),
          fetch(`https://api.aiassessment.uz/api/users/${course.author.id}/`),
        ]);
        const custom = await r1.json();
        const userData = await r2.json();
        setAuthor({ ...(Array.isArray(custom) ? custom[0] : custom), ...userData });
      } catch (err) {
        setErrorAuthor(err.message);
      } finally {
        setLoadingAuthor(false);
      }
    })();
  }, [course]);

  /* ── Handlers ── */
  const handleComment = () => {
    if (!localStorage.getItem('token') || !user?.id) {
      toast.warn(t('alertLogin') || 'Пожалуйста, войдите в аккаунт.');
      return;
    }
    navigate(`/courseComment/${id}`);
  };

  const handleEnroll = async () => {
    if (enrolling) return;
    if (!localStorage.getItem('token') || !user?.id) {
      toast.warn(t('alertLogin') || 'Пожалуйста, войдите в аккаунт.');
      return;
    }
    const token = localStorage.getItem('token');
    const userId = user.id;
    try {
      setEnrolling(true);
      const checkRes = await fetch(
        `https://api.aiassessment.uz/api/user-kurs/?user=${userId}&kurs=${id}`,
        { headers: { Authorization: `Token ${token}`, Accept: 'application/json' } }
      );
      let already = false;
      try { const d = await checkRes.json(); already = Array.isArray(d) && d.length > 0; } catch {}
      if (already) { navigate(`/themes/${id}`); return; }

      const postRes = await fetch('https://api.aiassessment.uz/api/user-kurs/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}`, Accept: 'application/json' },
        body: JSON.stringify({ user: userId, kurs: parseInt(id, 10), mavzu: 1 }),
      });
      try { await postRes.json(); } catch {}
      if (!postRes.ok) { toast.error(t('enrollFailed') || 'Ошибка при записи на курс.'); return; }
      toast.success(t('enrollSuccess') || 'Вы успешно записались на курс!');
      navigate(`/themes/${id}`);
    } catch {
      toast.error(t('networkError') || 'Произошла ошибка. Попробуйте позже.');
    } finally {
      setEnrolling(false);
    }
  };

  if (!course) return <p>Курс не найден</p>;
  if (error) return <p>Ошибка: {error}</p>;
  if (errorDesc) return <p>Ошибка: {errorDesc}</p>;

  return (
    <div>

      {/* ══════════════════════════════
          HERO
          ══════════════════════════════ */}
      <section className={s.hero}>
        <div className={s.heroTop}>

          {/* Left — text */}
          <div className={s.heroContent}>
            <span className={s.heroBadge}>
              {course.duration} {t('courseSubtitle') || ''}
            </span>
            <h1 className={s.heroTitle}>
              {handleLang(course.title_uz, course.title_ru, course.title_en)}
            </h1>
            <div className={s.heroButtons}>
              <button
                className={s.btnPrimary}
                onClick={handleEnroll}
                disabled={enrolling}
                aria-busy={enrolling}
              >
                <span>
                  {enrolling ? (t('processing') || 'Обработка…') : (t('courseLink') || 'Записаться на курс')}
                </span>
              </button>
              <button className={s.btnSecondary} onClick={handleComment}>
                {t('courseRate') || 'Оценить курс'}
              </button>
            </div>
          </div>

          {/* Right — image */}
          <div className={s.heroImageWrapper}>
            <img className={s.heroImage} src={course.img_uz} alt="" />
          </div>
        </div>

        {/* Stats bar */}
        <div className={s.statsBar}>
          <div className={s.statItem}>
            <span className={s.statNum}>{t('statMonth')}</span>
            <span className={s.statLabel}>{t('program1') || 'месяцев'}</span>
          </div>
          <div className={s.statItem}>
            <span className={s.statNum}>{t('statsModulesNum')}+</span>
            <span className={s.statLabel}>{t('statsModules') || 'модулей'}</span>
          </div>
          <div className={s.statItem}>
            <span className={s.statNum}>100%</span>
            <span className={s.statLabel}>{t('statsOnline') || 'онлайн'}</span>
          </div>
          <div className={s.statItem}>
            <span className={s.statNum}>24/7</span>
            <span className={s.statLabel}>{t('statsSupport') || 'поддержка'}</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════
          FEATURES — numbered list
          ══════════════════════════════ */}
      <section className={s.features}>
        {avzalliklar.length > 0 && (
          <>
            <div ref={featuresHeaderRef} className={`${s.featuresHeader} ${s.reveal}`}>
              <h2 className={s.featuresHeading}>
                {t('whyThisCourse') || 'Что вы получите от курса'}
              </h2>
              <span className={s.featuresCount}>0{avzalliklar.length}</span>
            </div>
            <div className={s.featuresList}>
              {avzalliklar.map((avz, index) => (
                <div
                  key={avz.id}
                  ref={el => featureItemRefs.current[index] = el}
                  className={s.featureItem}
                >
                  <div className={s.featureNum}>
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <p className={s.featureTitle}>
                    {handleLang(avz.title_uz, avz.title_ru, avz.title_en)}
                  </p>
                  <p className={s.featureText}>
                    {handleLang(avz.desc_uz, avz.desc_ru, avz.desc_en)}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ══════════════════════════════
          DESCRIPTION — pull quote style
          ══════════════════════════════ */}
      <section ref={descRef} className={`${s.descSection} ${s.reveal}`}>
        {(description.title_uz || description.desc_uz) && (
          <>
            <span className={s.descLabel}>{t('aboutCourse') || 'О курсе'}</span>
            <h2 className={s.sectionTitle}>
              {handleLang(description.title_uz, description.title_ru, description.title_en)}
            </h2>
            <div className={s.descLayout}>
              {description.img_uz && (
                <div className={s.descImageWrapper}>
                  <img className={s.descImage} src={description.img_uz} alt="" />
                </div>
              )}
              <div className={s.descTextBlock}>
                <span className={s.descQuoteMark}>"</span>
                <p className={s.descText}>
                  {handleLang(description.desc_uz, description.desc_ru, description.desc_en)}
                </p>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ══════════════════════════════
          COMMENTS SLIDER
          ══════════════════════════════ */}
      <SliderBigText />

      {/* ══════════════════════════════
          AUTHOR
          ══════════════════════════════ */}
      <section ref={authorRef} className={`${s.authorSection} ${s.reveal}`}>
        <span className={s.authorSectionLabel}>{t('author') || 'Автор'}</span>
        <h2 className={s.authorSectionTitle}>
          {t('meetAuthor') || 'Познакомьтесь с преподавателем'}
        </h2>
        {loadingAuthor && <p style={{ color: '#888' }}>...</p>}
        {errorAuthor && <p style={{ color: '#ff4d4d' }}>Ошибка: {String(errorAuthor)}</p>}
        {author && (
          <div className={s.authorCard}>
            <div className={s.authorAvatarWrapper}>
              <img
                src="/images/author.jpg"
                alt=""
                className={s.authorAvatar}
              />
            </div>
            <div>
              <p className={s.authorName}>
                {author.first_name} {author.last_name}
              </p>
              <p className={s.authorDesc}>{author.description || ''}</p>
            </div>
          </div>
        )}
      </section>

      {/* ══════════════════════════════
          TRAINING PROGRAM
          ══════════════════════════════ */}
      <TrainingProgram />
    </div>
  );
};
